<template>
  <modal
    name="edit-validation-modal"
    :classes="'customModal'"
    :scrollable="true"
    :reset="true"
    :adaptive="false"
    :width="650"
    height="auto"
    @closed="closeModal()"
    @before-open="getData"
  >
    <div class="modalWrapper">
      <div class="content__box">
        <div class="content__box__header">
          <p class="text-white txt--ss opacity--50">Edit Validation</p>
        </div>
        <div class="content__divider"></div>
        <div class="content__box__body">
          <div class="form-group">
            <label class="txt--xs txt--off-white mb-2">
              Validation name
              <i
                class="fa fa-info-circle ml-1"
                v-tooltip="'The name of the validation that will be shown to customers when they redeem it.'">
              </i>
            </label>
            <input
              type="text"
              class="textInputField"
              placeholder="Enter a validation name"
              v-model="validation_name"
            />
          </div>
          <div class="form-group">
            <label class="txt--xs txt--off-white">
              Validation price
              <i
                class="fa fa-info-circle ml-1"
                v-tooltip="'The price of a validation for internal use (not shown to customers).'">
              </i>
            </label>
            <div class="input-group-prfix">
              <span class="input-group-prefix-text">$</span
              ><input
                type="text"
                placeholder="Enter validation price"
                class="textInputField"
                v-model="validation_price"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="durationExpRange" class="txt--xs txt--off-white">
              Time to redeem
              <i
                class="fa fa-info-circle ml-1"
                v-tooltip="'After this amount of time, unused validations will expire and become available again.'"
              ></i>
            </label>
            <div class="input-group-prfix">
              <input
                id="durationExpRange"
                type="number"
                placeholder="Enter time to redeem"
                class="textInputField"
                v-model.number="durationExpRange"
                min="1"
                step="1"
              />
              <span class="input-group-suffix-text">
                <select class="hidden_input" v-model="expiry_duration_type">
                  <option v-for="opt in expiry_duration_type_options" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                </select>
              </span>
            </div>
          </div>

          <div class="form-group mt-4 modified-toggle-switch">
            <div class="d-flex align-items-center flex-wrap mb-2">
              <p class="txt--xs txt--off-white">
                Valet validation
                <i
                  class="fa fa-info-circle ml-1"
                  v-tooltip="'Whether validation is used to validate valet parking.'"
                ></i>
              </p>

              <app-toggle 
                :checked = "valet_validation" 
                :label = "'Enabled'"
                @update:checked="getCheckedValidity($event, 'valet_validation')"
              >
              </app-toggle>
            </div>
          </div>

          <div class="form-group modified-toggle-switch">
            <div class="d-flex align-items-center flex-wrap mb-2">
              <p class="txt--xs txt--off-white">
                Valid properties
                <i
                  class="fa fa-info-circle ml-1"
                  v-tooltip="'Properties at which parkers are allowed to use this validation.'"
                ></i>
              </p>

              <app-toggle
                :checked="all_properties"
                :label="'All Properties'"
                @update:checked="getCheckedStatus($event, 'all_properties')"
              >
              </app-toggle>
            </div>
          </div>

          <div class="form-group modified-toggle-switch" v-if="all_properties === false">
            <div class="d-flex align-items-center mb-2">
              <multiselect
                v-model="selected_property_list"
                class="multi-select-input"
                :options="property_list"
                :multiple="true"
                placeholder="Select Properties"
                track-by="property_name"
                label="property_name"
              >
              </multiselect>
            </div>
          </div>

          <div class="form-group mb-2">
            <label class="txt--xs txt--off-white mb-2 mr-2">Discount types</label>
            <ul class="top__filter__ul top__filter__ul--btn">
              <li
                class="mb-0"
                :class="{ active: validation_type === 'duration' }"
                @click="setValidationType('duration')"
              >
                Time Duration
              </li>
              <li
                class="mb-0"
                :class="{ active: validation_type === 'window' }"
                @click="setValidationType('window')"
                v-tooltip="'Free parking for a specific time window (e.g. 11am to 2pm)'"
              >
                Time Window
              </li>
              <li
                class="mb-0"
                :class="{ active: validation_type === 'amount' && validation_amount_type === 'fixed' }"
                @click="setValidationType('amount', 'fixed')"
              >
                Fixed Amount
              </li>
              <li
                class="mb-0"
                :class="{ active: validation_type === 'amount' && validation_amount_type === 'percentage' }"
                @click="setValidationType('amount', 'percentage')"
              >
                Variable Amount
              </li>
            </ul>
          </div>

          <div class="form-group" v-if="validation_type === 'amount'">
            <div class="form-group" v-if="validation_amount_type === 'fixed'">
              <label class="txt--xs txt--off-white">Discount amount</label>
              <div class="input-group-prfix">
                <span class="input-group-prefix-text">$</span>
                <input
                  type="number"
                  placeholder="Enter fixed amount"
                  class="textInputField"
                  v-model.number="validation_amount_fixed"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div class="form-group" v-else>
              <label class="txt--xs txt--off-white">Discount percentage</label>
              <div class="input-group-prfix">
                <input
                  type="number"
                  placeholder="Enter percentage off"
                  class="textInputField"
                  v-model.number="validation_amount_percentage"
                  min="0"
                  max="100"
                  step="1"
                />
                <span class="input-group-suffix-text">%</span>
              </div>
            </div>
          </div>

          <div class="form-group" v-if="validation_type === 'duration'">
            <label for="durationRange" class="txt--xs txt--off-white">Discount duration</label>
            <div class="input-group-prfix">
              <input
                v-if="validation_duration_input_unit !== 'all_day'"
                id="durationRange"
                type="number"
                placeholder="Enter duration"
                class="textInputField"
                v-model.number="durationRange"
                min="1"
                :step="validation_duration_type === 'hours' ? 0.5 : 1"
              />
              <input
                v-else
                id="durationRange"
                type="text"
                class="textInputField all-day-display"
                :value="'Until 23:59:59'"
                disabled
              />
              <span class="input-group-suffix-text">
                <select
                  class="hidden_input"
                  v-model="validation_duration_input_unit"
                >
                  <option v-for="opt in validation_duration_input_unit_options" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                </select>
              </span>
            </div>
          </div>

          <div class="form-group" v-if="validation_type === 'window'">
            <div class="form-row">
              <div class="col-sm-6">
                <div class="form-group mb-0">
                  <label class="txt--xs txt--off-white mb-2">
                    Discount start time
                  </label>
                  <input
                    type="time"
                    step="1"
                    class="textInputField"
                    v-model="validation_window_time_start"
                    @blur="validation_window_time_start = normalizeTimeToHms(validation_window_time_start)"
                  />
                </div>
              </div>
              <div class="col-sm-6">
                <div class="form-group mb-0">
                  <label class="txt--xs txt--off-white mb-2">
                    Discount end time
                    <i
                      class="fa fa-info-circle ml-1"
                      v-tooltip="'Overnight windows are allowed (e.g. 22:00:00 to 02:00:00).'"
                    ></i>
                  </label>
                  <input
                    type="time"
                    step="1"
                    class="textInputField"
                    v-model="validation_window_time_end"
                    @blur="validation_window_time_end = normalizeTimeToHms(validation_window_time_end)"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="form-group mt-4">
            <div class="d-flex align-items-center justify-content-between flex-wrap mb-2">
              <p class="txt--xs txt--off-white">
                Discount eligibility
                <i
                  class="fa fa-info-circle ml-1"
                  v-tooltip="'Restrict which sessions can have a validation applied'"
                ></i>
              </p>
              <app-toggle
                :checked="validation_discountable_duration_enabled"
                :label="'Enabled'"
                @update:checked="getCheckedStatus($event, 'validation_discountable_duration_enabled')"
              ></app-toggle>
            </div>

            <div class="form-row" v-if="validation_discountable_duration_enabled == true">
              <div class="col-sm-6">
                <div class="form-group mb-0">
                  <label class="txt--xs txt--off-white mb-2">Minimum duration (required)</label>
                  <div class="input-group-prfix">
                    <input
                      type="number"
                      placeholder="Enter minimum duration"
                      class="textInputField"
                      v-model.number="validation_discountable_duration_minimum"
                      min="0"
                      :step="validation_discountable_duration_type === 'hours' ? 0.5 : 1"
                    />
                    <span class="input-group-suffix-text">
                      <select class="hidden_input" v-model="validation_discountable_duration_type">
                        <option v-for="opt in validation_duration_type_options" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                      </select>
                    </span>
                  </div>
                </div>
              </div>
              <div class="col-sm-6">
                <div class="form-group mb-0">
                  <label class="txt--xs txt--off-white mb-2">Maximum duration (required)</label>
                  <div class="input-group-prfix">
                    <input
                      type="number"
                      placeholder="Enter maximum duration"
                      class="textInputField"
                      v-model.number="validation_discountable_duration_maximum"
                      min="0"
                      :step="validation_discountable_duration_type === 'hours' ? 0.5 : 1"
                    />
                    <span class="input-group-suffix-text">
                      <select class="hidden_input" v-model="validation_discountable_duration_type">
                        <option v-for="opt in validation_duration_type_options" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                      </select>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-group mt-4">
            <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
              <p class="txt--xs txt--off-white">
                Usage limitations
                <i
                  class="fa fa-info-circle ml-1"
                  v-tooltip="'Control how many validations can be used per unit of time.'"
                ></i>
              </p>
              <app-toggle
                :checked="validation_limit_enabled"
                :label="'Enabled'"
                @update:checked="getCheckedStatus($event, 'validation_limit_enabled')"
              ></app-toggle>
            </div>
            
            <div class="form-group" v-if="validation_limit_enabled == true">
              <div class="form-group mb-2">
                <ul class="top__filter__ul top__filter__ul--btn">
                  <li
                    class="mb-0"
                    :class="{ active: validation_limit_type === 'amount' }"
                    @click="setUsageLimitationType('amount')"
                  >
                    Amount
                  </li>
                  <li
                    class="mb-0"
                    :class="{ active: validation_limit_type === 'count' }"
                    @click="setUsageLimitationType('count')"
                  >
                    Count
                  </li>
                  <li
                    class="mb-0"
                    :class="{ active: validation_limit_type === 'rolling' }"
                    @click="setUsageLimitationType('rolling')"
                  >
                    Rolling
                  </li>
                </ul>
              </div>

              <div class="form-row" v-if="validation_limit_type == 'count'">
                <div class="col-sm-6">
                  <div class="form-group">
                    <label class="txt--xs txt--off-white mb-2">
                      Maximum number
                      <i
                        class="fa fa-info-circle ml-1"
                        v-tooltip="'Limit the number of validations that are being used within a given timeframe.'"
                      ></i>
                    </label>
                    <div class="input-group-prfix">
                      <input
                        type="number"
                        placeholder="Number of validations used"
                        class="textInputField"
                        v-model.number="validation_limit_count_maximum"
                        min="0"
                        step="1"
                      />
                      <span class="input-group-suffix-text">
                        <select class="hidden_input" v-model="validation_limit_count_timeframe">
                          <option v-for="opt in validation_limit_count_timeframe_options" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                        </select>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="form-row" v-if="validation_limit_type == 'amount'">
                <div class="col-sm-6">
                  <div class="form-group">
                    <label class="txt--xs txt--off-white mb-2">
                      Maximum amount
                      <i
                        class="fa fa-info-circle ml-1"
                        v-tooltip="'Limit the amount of validations that are being used within a given timeframe.'"
                      ></i>
                    </label>
                    <div class="input-group-prfix">
                      <span class="input-group-prefix-text">$</span>
                      <input
                        type="number"
                        placeholder="Amount of validations used"
                        class="textInputField"
                        v-model.number="validation_limit_amount_maximum"
                        min="0"
                        step="0.01"
                      />
                      <span class="input-group-suffix-text">
                        <select class="hidden_input" v-model="validation_limit_amount_timeframe">
                          <option v-for="opt in validation_limit_amount_timeframe_options" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                        </select>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="form-row" v-if="validation_limit_type == 'rolling'">
                <div class="col-sm-6">
                  <div class="form-group">
                    <label class="txt--xs txt--off-white mb-2">
                      Maximum number
                      <i
                        class="fa fa-info-circle ml-1"
                        v-tooltip="'Limit the number of validations available on a rolling basis.'"
                      ></i>
                    </label>
                    <input
                      type="number"
                      placeholder="Number of rolling validations"
                      class="textInputField"
                      v-model.number="validation_limit_rolling_maximum"
                      min="0"
                      step="1"
                    />
                  </div>
                </div>
              </div>

              <div class="form-group modified-toggle-switch">
                <div class="d-flex align-items-center justify-content-between flex-wrap mb-2">
                  <p class="txt--xs txt--off-white">
                    Allow limit to be exceeded
                    <i
                      class="fa fa-info-circle ml-1"
                      v-tooltip="'If enabled, parkers will be allowed to redeem validations even if usage limits have been exceeded. If disabled, once usage limits are reached, parkers will not be able to redeem validations until the usage within the relevant timeframe drops below the limit.'"
                    ></i>
                  </p>
                  <app-toggle
                    :checked="validation_limit_exceedable"
                    :label="'Enabled'"
                    @update:checked="getCheckedStatus($event, 'validation_limit_exceedable')"
                  ></app-toggle>
                </div>
              </div>
            </div>
          </div>

          <div class="form-group mt-4">
            <div class="d-flex align-items-center justify-content-between flex-wrap mb-2">
              <p class="txt--xs txt--off-white">
                Day restriction
                <i
                  class="fa fa-info-circle ml-1"
                  v-tooltip="'Restrict which days of the week a validation can be redeemed.'"
                ></i>
              </p>
              <app-toggle
                :checked="validation_day_restriction_enabled"
                :label="'Enabled'"
                @update:checked="getCheckedStatus($event, 'validation_day_restriction_enabled')"
              ></app-toggle>
            </div>

            <div v-if="validation_day_restriction_enabled == true">
              <label class="txt--xs txt--off-white mb-2">Allowed days</label>
              <multiselect
                v-model="validation_allowed_days"
                class="multi-select-input"
                :options="validation_allowed_day_options"
                :multiple="true"
                placeholder="Select allowed days"
                track-by="value"
                label="text"
              >
              </multiselect>
            </div>
          </div>

          <div class="form-group mt-4">
            <div class="d-flex align-items-center justify-content-between flex-wrap mb-2">
              <p class="txt--xs txt--off-white">
                Time restriction
                <i
                  class="fa fa-info-circle ml-1"
                  v-tooltip="'Restrict the time window (local) when a validation can be redeemed.'"
                ></i>
              </p>
              <app-toggle
                :checked="validation_time_restriction_enabled"
                :label="'Enabled'"
                @update:checked="getCheckedStatus($event, 'validation_time_restriction_enabled')"
              ></app-toggle>
            </div>

            <div class="form-row" v-if="validation_time_restriction_enabled == true">
              <div class="col-sm-6">
                <div class="form-group mb-0">
                  <label class="txt--xs txt--off-white mb-2">Start time</label>
                  <input
                    type="time"
                    step="1"
                    class="textInputField"
                    v-model="validation_time_start"
                    @blur="validation_time_start = normalizeTimeToHms(validation_time_start)"
                  />
                </div>
              </div>
              <div class="col-sm-6">
                <div class="form-group mb-0">
                  <label class="txt--xs txt--off-white mb-2">
                    End time
                    <i
                      class="fa fa-info-circle ml-1"
                      v-tooltip="'Overnight windows are allowed (e.g. 22:00:00 to 02:00:00).'">
                    </i>
                  </label>
                  <input
                    type="time"
                    step="1"
                    class="textInputField"
                    v-model="validation_time_end"
                    @blur="validation_time_end = normalizeTimeToHms(validation_time_end)"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="form-group mt-4">
            <div class="d-flex align-items-center justify-content-between flex-wrap mb-2">
              <p class="txt--xs txt--off-white">
                Event restriction
                <i
                  class="fa fa-info-circle ml-1"
                  v-tooltip="'Restrict redemption to a specific date/time range.'"
                ></i>
              </p>
              <app-toggle
                :checked="validation_event_restriction_enabled"
                :label="'Enabled'"
                @update:checked="getCheckedStatus($event, 'validation_event_restriction_enabled')"
              ></app-toggle>
            </div>

            <div class="form-row" v-if="validation_event_restriction_enabled == true">
              <div class="col-sm-6">
                <div class="form-group mb-0">
                  <label class="txt--xs txt--off-white mb-2">Event start</label>
                  <input
                    type="datetime-local"
                    step="1"
                    class="textInputField"
                    v-model="validation_event_start_datetime"
                    @blur="validation_event_start_datetime = normalizeLocalDateTimeToSeconds(validation_event_start_datetime)"
                  />
                </div>
              </div>
              <div class="col-sm-6">
                <div class="form-group mb-0">
                  <label class="txt--xs txt--off-white mb-2">Event end</label>
                  <input
                    type="datetime-local"
                    step="1"
                    class="textInputField"
                    v-model="validation_event_end_datetime"
                    @blur="validation_event_end_datetime = normalizeLocalDateTimeToSeconds(validation_event_end_datetime)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
      </div>
      <div class="d-flex justify-content-center flex-wrap mt-4">
        <ButtonWithSpinner class="button button--primary button--sm mr-3" 
        :disabled="create_button.disabled"
        :loading="create_button.loading"
        :success="create_button.success"
        @click="updateValidation">
          Update
        </ButtonWithSpinner>
        
        <button
          type="button"
          class="button button--outline button--sm"
          @click="closeModal()"
        >
          Cancel
        </button>
      </div>
      <div class="session__modal__close" @click="closeModal()">
        <i class="fas fa-times"></i>
      </div>
    </div>
  </modal>
</template>
<script>
import ButtonWithSpinner from "@/components/Button";
import Toggle from "@/components/toggle";
export default {
  name: "EditValidation",
  components: {
    AppToggle: Toggle,
    ButtonWithSpinner,
  },
  data() {
    return {
      id:                     null,
      // value:                  null,
      properties:             true,
      // validation:             null,
      validity_time:          true,
      property_list:          [],
      all_properties:         true,
      validation_name:        '',
      validation_price:       null,
      unlimited_validaiton:   true,
      selected_property_list: null,
      duration_all_day:       true,

      // New validation policy fields
      validation_type: 'duration',
      validation_amount_type: 'fixed',
      validation_amount_fixed: 0,
      validation_amount_percentage: 0,
      validation_amount_fixed_operation: 'off',

      validation_window_time_start: '',
      validation_window_time_end: '',

      validation_discountable_duration_enabled: false,
      validation_discountable_duration_type: 'hours',
      validation_discountable_duration_minimum: 0,
      validation_discountable_duration_maximum: 0,

      validation_duration_type: 'hours',
      expiry_duration_type: 'minutes',
      validation_duration_type_options: [
        { value: 'hours', text: 'Hours' },
        { value: 'minutes', text: 'Minutes' },
      ],
      validation_duration_input_unit: 'all_day',
      validation_duration_input_unit_options: [
        { value: 'all_day', text: 'All Day' },
        { value: 'hours', text: 'Hours' },
        { value: 'minutes', text: 'Minutes' },
      ],
      durationRangeStoredMinutes: null,
      expiry_duration_type_options: [
        { value: 'minutes', text: 'Minutes' },
        { value: 'hours', text: 'Hours' },
      ],

      validation_limit_enabled: false,
      validation_limit_type: 'amount',
      validation_limit_exceedable: false,
      validation_limit_amount_maximum: 0,
      validation_limit_amount_timeframe: 'day',
      validation_limit_count_maximum: 0,
      validation_limit_count_timeframe: 'day',
      validation_limit_rolling_maximum: 0,
      validation_limit_amount_timeframe_options: [
        { value: 'day', text: 'Per day' },
        { value: 'week', text: 'Per week' },
        { value: 'month', text: 'Per month' },
      ],
      validation_limit_count_timeframe_options: [
        { value: 'day', text: 'Per day' },
        { value: 'week', text: 'Per week' },
        { value: 'month', text: 'Per month' },
      ],

      validation_day_restriction_enabled: false,
      validation_allowed_days: [],
      validation_allowed_day_options: [
        { value: 0, text: 'Sunday' },
        { value: 1, text: 'Monday' },
        { value: 2, text: 'Tuesday' },
        { value: 3, text: 'Wednesday' },
        { value: 4, text: 'Thursday' },
        { value: 5, text: 'Friday' },
        { value: 6, text: 'Saturday' },
      ],

      validation_time_restriction_enabled: false,
      validation_time_start: '',
      validation_time_end: '',

      validation_event_restriction_enabled: false,
      validation_event_start_datetime: '',
      validation_event_end_datetime: '',

      create_button: {
        disabled: false,
        loading: false,
        success: false,
      },
      durationRange:          0,
      durationExpRange :      15,
      valet_validation:       false,
      isHydrating:            false,
    };
  },

  created() {
      this.getProperties();
  },
  watch:{
    expiry_duration_type: function(newType, oldType) {
      if (this.isHydrating) return;
      if (!oldType || newType === oldType) return;
      const current = Number(this.durationExpRange);
      if (!Number.isFinite(current)) return;

      if (oldType === 'hours' && newType === 'minutes') {
        this.durationExpRange = Math.round(current * 60);
        return;
      }

      if (oldType === 'minutes' && newType === 'hours') {
        this.durationExpRange = Math.max(1, Math.ceil(current / 60));
      }
    },
    validation_duration_input_unit: function(newUnit, oldUnit) {
      if (this.isHydrating) return;
      if (!oldUnit || newUnit === oldUnit) return;

      const toMinutes = (value, unit) => {
        if (value === '' || value === null || value === undefined) return null;
        const numeric = Number(value);
        if (!Number.isFinite(numeric)) return null;
        return unit === 'hours' ? numeric * 60 : numeric;
      };
      const fromMinutes = (minutes, unit) => {
        if (minutes === '' || minutes === null || minutes === undefined) return null;
        const numeric = Number(minutes);
        if (!Number.isFinite(numeric)) return null;
        if (unit === 'hours') return Math.round((numeric / 60) * 2) / 2;
        return Math.round(numeric);
      };

      if (newUnit === 'all_day') {
        if (oldUnit === 'hours' || oldUnit === 'minutes') {
          this.durationRangeStoredMinutes = toMinutes(this.durationRange, oldUnit);
        }
        this.validity_time = true;
        this.validation_duration_type = 'hours';
        this.durationRange = '';
        return;
      }

      if (oldUnit === 'all_day') {
        this.validity_time = false;
        this.validation_duration_type = newUnit;
        if (this.durationRangeStoredMinutes !== null) {
          const restored = fromMinutes(this.durationRangeStoredMinutes, newUnit);
          this.durationRange = restored !== null ? restored : (newUnit === 'hours' ? 1 : 60);
        } else {
          this.durationRange = newUnit === 'hours' ? 1 : 60;
        }
        return;
      }

      if ((oldUnit === 'hours' || oldUnit === 'minutes') && (newUnit === 'hours' || newUnit === 'minutes')) {
        const minutes = toMinutes(this.durationRange, oldUnit);
        this.validation_duration_type = newUnit;
        const converted = minutes !== null ? fromMinutes(minutes, newUnit) : null;
        this.durationRange = converted !== null ? converted : this.durationRange;
        this.validity_time = false;
      }
    },
    validation_discountable_duration_type: function(newType, oldType) {
      if (this.isHydrating) return;
      if (!oldType || newType === oldType) return;

      const minVal = Number(this.validation_discountable_duration_minimum);
      const maxVal = Number(this.validation_discountable_duration_maximum);

      if (oldType === 'hours' && newType === 'minutes') {
        if (Number.isFinite(minVal)) this.validation_discountable_duration_minimum = Math.round(minVal * 60);
        if (Number.isFinite(maxVal)) this.validation_discountable_duration_maximum = Math.round(maxVal * 60);
        return;
      }

      if (oldType === 'minutes' && newType === 'hours') {
        if (Number.isFinite(minVal)) this.validation_discountable_duration_minimum = Math.round((minVal / 60) * 2) / 2;
        if (Number.isFinite(maxVal)) this.validation_discountable_duration_maximum = Math.round((maxVal / 60) * 2) / 2;
      }
    },
  },
  methods: {
    asBool(value) {
      if (value === true || value === false) return value;
      if (value === 1 || value === '1') return true;
      if (value === 0 || value === '0') return false;
      return Boolean(value);
    },
    normalizeAllowedDays(value) {
      if (Array.isArray(value)) {
        return value
          .map((v) => (typeof v === 'string' && v.trim() !== '' ? Number(v) : Number(v)))
          .filter((v) => Number.isInteger(v) && v >= 0 && v <= 6);
      }

      if (typeof value === 'string') {
        return value
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v !== '')
          .map((v) => Number(v))
          .filter((v) => Number.isInteger(v) && v >= 0 && v <= 6);
      }

      if (typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 6) {
        return [value];
      }

      return [];
    },
    setValidationType(type, subType) {
      if (type === 'amount') {
        this.validation_type = 'amount';
        if (subType === 'fixed') {
          this.validation_amount_type = 'fixed';
        } else if (subType === 'percentage') {
          this.validation_amount_type = 'percentage';
        } else {
          this.validation_amount_type = 'fixed';
        }
        return;
      }
      if (type === 'window') {
        this.validation_type = 'window';
        if (!this.validation_window_time_start) this.validation_window_time_start = '00:00:00';
        if (!this.validation_window_time_end) this.validation_window_time_end = '23:59:59';
        this.validation_window_time_start = this.normalizeTimeToHms(this.validation_window_time_start);
        this.validation_window_time_end = this.normalizeTimeToHms(this.validation_window_time_end);
        return;
      }
      this.validation_type = 'duration';
    },
    setUsageLimitationType(type) {
      if (type === 'amount' || type === 'count' || type === 'rolling') {
        this.validation_limit_type = type;
      }
    },
    getCheckedValidity(checked, type)
    {
      if(type == 'valet_validation')
      {
        this.valet_validation = checked;
      }
    },
    closeModal() {
      this.create_button.disabled = false;
      this.create_button.success = false;
      this.create_button.loading = false;
      this.$modal.hide("edit-validation-modal");
    },
    getData(e)
    {
      this.isHydrating = true;
      this.id                       = e.params.validation.id;
      this.status                   = e.params.validation.status;
      this.validity_time            = this.asBool(e.params.validation.duration_all_day);
      this.durationRange            = e.params.validation.validation_duration !== undefined && e.params.validation.validation_duration !== null ? Number(e.params.validation.validation_duration) : 0;
      this.validation_duration_type = e.params.validation.validation_duration_type || 'hours';
      this.durationExpRange         = e.params.validation.expiry_duration !== undefined && e.params.validation.expiry_duration !== null ? Number(e.params.validation.expiry_duration) : 15;
      this.expiry_duration_type     = e.params.validation.expiry_duration_type || 'minutes';
      this.all_properties           = e.params.validation.all_properties;
      this.validation_name          = e.params.validation.name;
      this.duration_all_day         = e.params.validation.duration_all_day;
      this.validation_price         = e.params.validation.price;
      this.validity_duration        = e.params.validation.validity_duration;
      this.validity_duration_type   = e.params.validation.validity_duration_type;
      this.selected_property_list   = e.params.validation.properties_list;

      // Sync duration input unit with the record
      if (this.validity_time === true) {
        const unit = (e.params.validation.validation_duration_type || 'hours') === 'minutes' ? 'minutes' : 'hours';
        const minutes = unit === 'hours' ? Number(this.durationRange) * 60 : Number(this.durationRange);
        this.durationRangeStoredMinutes = Number.isFinite(minutes) ? minutes : 24 * 60;
        this.validation_duration_input_unit = 'all_day';
        this.validation_duration_type = 'hours';
        this.durationRange = '';
      } else {
        this.validation_duration_input_unit = this.validation_duration_type;
        const minutes = this.validation_duration_type === 'hours' ? Number(this.durationRange) * 60 : Number(this.durationRange);
        this.durationRangeStoredMinutes = Number.isFinite(minutes) ? minutes : null;
      }

      // New policy fields
      this.validation_type = e.params.validation.validation_type || 'duration';
      this.validation_amount_type = e.params.validation.validation_amount_type || 'fixed';
      this.validation_amount_fixed = Number(e.params.validation.validation_amount_fixed ?? 0);
      this.validation_amount_percentage = Number(e.params.validation.validation_amount_percentage ?? 0);
      this.validation_amount_fixed_operation = e.params.validation.validation_amount_fixed_operation || 'off';

      this.validation_window_time_start = this.normalizeTimeToHms(e.params.validation.validation_window_time_start || '');
      this.validation_window_time_end = this.normalizeTimeToHms(e.params.validation.validation_window_time_end || '');

      this.validation_discountable_duration_enabled = this.asBool(e.params.validation.validation_discountable_duration_enabled);
      this.validation_discountable_duration_type = e.params.validation.validation_discountable_duration_type || 'hours';
      this.validation_discountable_duration_minimum = Number(e.params.validation.validation_discountable_duration_minimum ?? 0);
      this.validation_discountable_duration_maximum = Number(e.params.validation.validation_discountable_duration_maximum ?? 0);

      this.validation_limit_enabled = this.asBool(e.params.validation.validation_limit_enabled);
      this.validation_limit_type = e.params.validation.validation_limit_type || 'amount';
      this.validation_limit_exceedable = this.asBool(e.params.validation.validation_limit_exceedable);
      this.validation_limit_amount_maximum = Number(e.params.validation.validation_limit_amount_maximum ?? 0);
      this.validation_limit_amount_timeframe = e.params.validation.validation_limit_amount_timeframe || 'day';
      this.validation_limit_count_maximum = Number(e.params.validation.validation_limit_count_maximum ?? 0);
      this.validation_limit_count_timeframe = e.params.validation.validation_limit_count_timeframe || 'day';
      this.validation_limit_rolling_maximum = Number(e.params.validation.validation_limit_rolling_maximum ?? 0);

      this.valet_validation         = this.asBool(e.params.validation.valet_validation);

      this.validation_day_restriction_enabled = this.asBool(e.params.validation.validation_day_restriction_enabled);
      const allowedDaysRaw = e.params.validation.validation_allowed_days;
      const allowedDays = this.normalizeAllowedDays(allowedDaysRaw);
      this.validation_allowed_days = this.validation_allowed_day_options.filter((opt) => allowedDays.includes(opt.value));

      this.validation_time_restriction_enabled = this.asBool(e.params.validation.validation_time_restriction_enabled);
      this.validation_time_start = this.normalizeTimeToHms(e.params.validation.validation_time_start || '');
      this.validation_time_end = this.normalizeTimeToHms(e.params.validation.validation_time_end || '');

      this.validation_event_restriction_enabled = this.asBool(e.params.validation.validation_event_restriction_enabled);
      this.validation_event_start_datetime = this.normalizeLocalDateTimeToSeconds(e.params.validation.validation_event_start_datetime || '');
      this.validation_event_end_datetime = this.normalizeLocalDateTimeToSeconds(e.params.validation.validation_event_end_datetime || '');

      for(var i = 0; i < this.property_list.length; i++)
      {
        for(var j = 0; j < this.selected_property_list.length; j++)
        {
          if(this.property_list[i]['space_id'] == this.selected_property_list[j])
          {
            this.selected_property_list[j] = this.property_list[i];
          }
        }
      }

      this.$nextTick(() => {
        this.isHydrating = false;
      });


    },
    getCheckedStatus(checked, type)
    {
      if(type == 'all_properties')
      {
        this.all_properties         = checked;
        if(checked==true){
          this.selected_property_list = null;
        }
      }

      if (type == 'validation_discountable_duration_enabled') {
        this.validation_discountable_duration_enabled = checked;
      }

      if (type == 'validation_limit_enabled') {
        this.validation_limit_enabled = checked;
      }

      if (type == 'validation_limit_exceedable') {
        this.validation_limit_exceedable = checked;
      }

      if (type == 'validation_day_restriction_enabled') {
        this.validation_day_restriction_enabled = checked;
        if (!checked) {
          this.validation_allowed_days = [];
        }
      }

      if (type == 'validation_time_restriction_enabled') {
        this.validation_time_restriction_enabled = checked;
        if (checked) {
          if (!this.validation_time_start) this.validation_time_start = '00:00:00';
          if (!this.validation_time_end) this.validation_time_end = '23:59:59';
          this.validation_time_start = this.normalizeTimeToHms(this.validation_time_start);
          this.validation_time_end = this.normalizeTimeToHms(this.validation_time_end);
        } else {
          this.validation_time_start = '';
          this.validation_time_end = '';
        }
      }

      if (type == 'validation_event_restriction_enabled') {
        this.validation_event_restriction_enabled = checked;
        if (!checked) {
          this.validation_event_start_datetime = '';
          this.validation_event_end_datetime = '';
        }
      }
    },

    normalizeTimeToHms(timeValue) {
      if (!timeValue) return '';
      const value = String(timeValue).trim();
      if (/^\d{2}:\d{2}$/.test(value)) return `${value}:00`;
      if (/^\d{2}:\d{2}:\d{2}$/.test(value)) return value;
      return value;
    },

    normalizeLocalDateTimeToSeconds(dateTimeValue) {
      if (!dateTimeValue) return '';
      let value = String(dateTimeValue).trim();

      // Strip timezone suffix if pasted (datetime-local normally won't include it)
      value = value.replace(/(Z|[+-]\d{2}:\d{2})$/i, '');

      // Drop fractional seconds if present
      value = value.replace(/\.\d+$/, '');

      // Add seconds if missing
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return `${value}:00`;
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(value)) return value;

      return value;
    },
    updateValidation()
    {
      if(this.validation_name == '')
      {
          this.$toasted.show('Please type validation name !', {
            icon: "times-circle",
            className: "error-class",
          });
          return false;
      }
      if(this.validation_price == '')
      {
          this.$toasted.show('Please type validation price !', {
            icon: "times-circle",
            className: "error-class",
          });
          return false;
      }
      if(this.validation_type === 'duration' && this.validity_time == false && (this.durationRange === '' || this.durationRange === null))
      {
          this.$toasted.show('Please type validation duration!', {
            icon: "times-circle",
            className: "error-class",
          });
          return false;
      }

      if(this.durationExpRange == '')
      {
          this.$toasted.show('Please type validation expiry duration!', {
            icon: "times-circle",
            className: "error-class",
          });
          return false;
      }

      if (this.validation_type === 'amount') {
        if (this.validation_amount_type === 'fixed' && (this.validation_amount_fixed === null || this.validation_amount_fixed === '' || Number(this.validation_amount_fixed) < 0)) {
          this.$toasted.show('Please enter a valid fixed discount amount!', {
            icon: "times-circle",
            className: "error-class",
          });
          return false;
        }
        if (this.validation_amount_type === 'percentage' && (this.validation_amount_percentage === null || this.validation_amount_percentage === '' || Number(this.validation_amount_percentage) < 0 || Number(this.validation_amount_percentage) > 100)) {
          this.$toasted.show('Please enter a valid discount percentage (0-100)!', {
            icon: "times-circle",
            className: "error-class",
          });
          return false;
        }
      }

      if (this.validation_type === 'window') {
        const normalizedWindowStart = this.normalizeTimeToHms(this.validation_window_time_start);
        const normalizedWindowEnd = this.normalizeTimeToHms(this.validation_window_time_end);
        this.validation_window_time_start = normalizedWindowStart;
        this.validation_window_time_end = normalizedWindowEnd;

        if (!normalizedWindowStart || !normalizedWindowEnd) {
          this.$toasted.show('Please select both a window start and end time!', {
            icon: "times-circle",
            className: "error-class",
          });
          return false;
        }
      }

      if (this.validation_discountable_duration_enabled == true) {
        if (Number(this.validation_discountable_duration_minimum) < 0 || Number(this.validation_discountable_duration_maximum) < 0) {
          this.$toasted.show('Discount eligibility durations must be 0 or greater!', {
            icon: "times-circle",
            className: "error-class",
          });
          return false;
        }
        if (Number(this.validation_discountable_duration_maximum) > 0 && Number(this.validation_discountable_duration_minimum) > Number(this.validation_discountable_duration_maximum)) {
          this.$toasted.show('Minimum duration cannot exceed maximum duration!', {
            icon: "times-circle",
            className: "error-class",
          });
          return false;
        }
      }

      if (this.validation_limit_enabled == true) {
        if (this.validation_limit_type === 'amount') {
          if (!this.validation_limit_amount_timeframe) {
            this.$toasted.show('Please select an amount limit timeframe!', {
              icon: "times-circle",
              className: "error-class",
            });
            return false;
          }
        }
        if (this.validation_limit_type === 'count') {
          if (!this.validation_limit_count_timeframe) {
            this.$toasted.show('Please select a count limit timeframe!', {
              icon: "times-circle",
              className: "error-class",
            });
            return false;
          }
        }
      }

      if (this.validation_day_restriction_enabled == true) {
        if (!Array.isArray(this.validation_allowed_days) || this.validation_allowed_days.length === 0) {
          this.$toasted.show('Please select at least one allowed day!', {
            icon: "times-circle",
            className: "error-class",
          });
          return false;
        }
      }

      if (this.validation_time_restriction_enabled == true) {
        if (!this.validation_time_start || !this.validation_time_end) {
          this.$toasted.show('Please select both a start and end time!', {
            icon: "times-circle",
            className: "error-class",
          });
          return false;
        }
      }

      if (this.validation_event_restriction_enabled == true) {
        if (!this.validation_event_start_datetime || !this.validation_event_end_datetime) {
          this.$toasted.show('Please select both an event start and end date/time!', {
            icon: "times-circle",
            className: "error-class",
          });
          return false;
        }
        const eventStart = this.normalizeLocalDateTimeToSeconds(this.validation_event_start_datetime);
        const eventEnd = this.normalizeLocalDateTimeToSeconds(this.validation_event_end_datetime);
        if (eventStart && eventEnd && eventStart > eventEnd) {
          this.$toasted.show('Event start cannot be after event end!', {
            icon: "times-circle",
            className: "error-class",
          });
          return false;
        }
      }

      this.create_button.loading = true;
      this.create_button.disabled = true;
      this.create_button.success = false;

      this.axios
        .post(window.hostname + "api/auth/validation/updateValidation", {
          id:                       this.id,
          name:                     this.validation_name,
          price:                    this.validation_price,
          device_type:              1,
          validity_time:            this.validity_time,
          duration_all_day:          this.validity_time,
          property_list:            this.selected_property_list,
          all_properties:           this.all_properties,
          validation_duration:      this.validity_time ? '' : this.durationRange,
          validation_duration_type: this.validity_time ? 'hours' : this.validation_duration_type,
          expiry_duration:          this.durationExpRange,
          expiry_duration_type:     this.expiry_duration_type,
          valet_validation:         this.valet_validation ? 1 : 0,

          validation_type: this.validation_type,
          validation_amount_type: this.validation_amount_type,
          validation_amount_fixed: this.validation_amount_fixed,
          validation_amount_percentage: this.validation_amount_percentage,
          validation_amount_fixed_operation: this.validation_amount_fixed_operation,

          validation_window_time_start: this.validation_type === 'window' ? this.normalizeTimeToHms(this.validation_window_time_start) : null,
          validation_window_time_end: this.validation_type === 'window' ? this.normalizeTimeToHms(this.validation_window_time_end) : null,

          validation_discountable_duration_enabled: this.validation_discountable_duration_enabled,
          validation_discountable_duration_type: this.validation_discountable_duration_type,
          validation_discountable_duration_minimum: this.validation_discountable_duration_minimum,
          validation_discountable_duration_maximum: this.validation_discountable_duration_maximum,

          validation_limit_enabled: this.validation_limit_enabled,
          validation_limit_type: this.validation_limit_type,
          validation_limit_exceedable: this.validation_limit_exceedable,
          validation_limit_amount_maximum: this.validation_limit_amount_maximum,
          validation_limit_amount_timeframe: this.validation_limit_amount_timeframe,
          validation_limit_count_maximum: this.validation_limit_count_maximum,
          validation_limit_count_timeframe: this.validation_limit_count_timeframe,
          validation_limit_rolling_maximum: this.validation_limit_rolling_maximum,

          validation_day_restriction_enabled: this.validation_day_restriction_enabled,
          validation_allowed_days: this.validation_day_restriction_enabled ? this.validation_allowed_days.map((d) => d.value) : [],

          validation_time_restriction_enabled: this.validation_time_restriction_enabled,
          validation_time_start: this.validation_time_restriction_enabled ? this.normalizeTimeToHms(this.validation_time_start) : null,
          validation_time_end: this.validation_time_restriction_enabled ? this.normalizeTimeToHms(this.validation_time_end) : null,

          validation_event_restriction_enabled: this.validation_event_restriction_enabled,
          validation_event_start_datetime: this.validation_event_restriction_enabled ? this.normalizeLocalDateTimeToSeconds(this.validation_event_start_datetime) : null,
          validation_event_end_datetime: this.validation_event_restriction_enabled ? this.normalizeLocalDateTimeToSeconds(this.validation_event_end_datetime) : null,
        })
        .then((res) => {
          this.$emit("update_validation_list");
          this.$toasted.show('Validation updated successfully !', {
              icon: "check-circle",
              className: "success-class",
            });
          this.create_button.disabled = false;
          this.create_button.success = true;
          this.create_button.loading = false;

          this.closeModal();
        })
        .catch((err) => {
          this.$toasted.show(err.data.message, {
              icon: "check-circle",
              className: "error-class",
            });
          this.create_button.disabled = false;
          this.create_button.success = false;
          this.create_button.loading = false;
        });

    },
    getProperties()
    {
      this.axios
        .post(window.hostname + "api/auth/all-space", {
          limit: 500,
          device_type: 1,
          space_type: 1,
          status: 0,
          page: 1,
          minify: 1,
          minify_2: 0,
          time_zone: this.$moment.tz.guess(new Date()),
        })
        .then((res) => {
          if (res.data.status) {
            this.property_list = res.data.data.all_space;
          } else {
            this.property_list = [];
          }
        })
        .catch((err) => {
          this.property_list = [];
        });
    },
  },
};
</script>

<style scoped>
.modified-toggle-switch p {
  flex-basis: 50%;
}

.modified-toggle-switch app-toggle {
  flex-basis: 50%;
}

.textInputField.all-day-display:disabled {
  opacity: 1;
  color: rgba(255, 255, 255, 0.55);
  -webkit-text-fill-color: rgba(255, 255, 255, 0.55);
}
</style>